import React, { useState, useRef, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  mass: number;
}

interface Attractor {
  x: number;
  y: number;
  mass: number;
}

export function GravityParticleCollapse() {
  const [gravity, setGravity] = useState<number>(1);
  const particlesRef = useRef<Particle[]>([]);
  const attractorsRef = useRef<Attractor[]>([]);

  const initParticles = (width: number, height: number) => {
    const list: Particle[] = [];
    for (let i = 0; i < 200; i++) {
      list.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        mass: 0.8 + Math.random() * 0.4
      });
    }

    // Compute 4 density centres (attractors) by averaging quadrant coordinates
    const q1 = list.filter(p => p.x < width / 2 && p.y < height / 2);
    const q2 = list.filter(p => p.x >= width / 2 && p.y < height / 2);
    const q3 = list.filter(p => p.x < width / 2 && p.y >= height / 2);
    const q4 = list.filter(p => p.x >= width / 2 && p.y >= height / 2);

    const getAverage = (quadrant: Particle[], defaultX: number, defaultY: number): Attractor => {
      if (quadrant.length === 0) return { x: defaultX, y: defaultY, mass: 1 };
      let sumX = 0, sumY = 0;
      quadrant.forEach(p => {
        sumX += p.x;
        sumY += p.y;
      });
      return {
        x: sumX / quadrant.length,
        y: sumY / quadrant.length,
        mass: 1.0
      };
    };

    const attractorsList: Attractor[] = [
      getAverage(q1, width * 0.25, height * 0.25),
      getAverage(q2, width * 0.75, height * 0.25),
      getAverage(q3, width * 0.25, height * 0.75),
      getAverage(q4, width * 0.75, height * 0.75)
    ];

    return { particles: list, attractors: attractorsList };
  };

  const handleReset = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const { particles, attractors } = initParticles(width, height);
    particlesRef.current = particles;
    attractorsRef.current = attractors;
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Lazy initialization on first frame once size is known
    if (particlesRef.current.length === 0) {
      const { particles, attractors } = initParticles(width, height);
      particlesRef.current = particles;
      attractorsRef.current = attractors;
    }

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const particles = particlesRef.current;
    const attractors = attractorsRef.current;

    // Draw Attractor Glow if gravity is strong
    if (gravity > 3) {
      attractors.forEach(attr => {
        const grad = ctx.createRadialGradient(attr.x, attr.y, 0, attr.x, attr.y, 40);
        grad.addColorStop(0, 'rgba(34, 211, 238, 0.15)');
        grad.addColorStop(1, 'rgba(34, 211, 238, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(attr.x, attr.y, 40, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Physics Update and Render Particles
    particles.forEach(p => {
      // Sum forces toward all attractors
      attractors.forEach(attr => {
        const dx = attr.x - p.x;
        const dy = attr.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy) + 10; // prevent singularity

        // Gravity attraction force
        const force = (gravity * attr.mass) / (dist * dist);
        p.vx += (dx / dist) * force * deltaTime * 200;
        p.vy += (dy / dist) * force * deltaTime * 200;
      });

      // Damping (air resistance)
      p.vx *= 0.98;
      p.vy *= 0.98;

      // Update positions
      p.x += p.vx;
      p.y += p.vy;

      // Collision boundary checks
      if (p.x < 0) {
        p.x = 0;
        p.vx = -p.vx;
      } else if (p.x > width) {
        p.x = width;
        p.vx = -p.vx;
      }

      if (p.y < 0) {
        p.y = 0;
        p.vy = -p.vy;
      } else if (p.y > height) {
        p.y = height;
        p.vy = -p.vy;
      }

      // Render particle
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const t = Math.min(speed / 4, 1);
      
      // Interpolate colors: slow (light blue) to fast (orange/yellow)
      const r = Math.round(100 + t * 155);
      const g = Math.round(150 + t * 50);
      const b = Math.round(255 - t * 155);
      const a = 0.6 + t * 0.3;

      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Gravity & Cosmic Dust Collapse Simulation', 12, 24);
  }, [gravity]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10">
      <canvas ref={canvasRef} className="block w-full h-full" />
      
      {/* HTML absolute overlay for sliders */}
      <div className="absolute bottom-3 left-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-2 max-w-[280px]">
        <span className="text-white text-xs font-semibold">
          Gravity Strength: {gravity.toFixed(1)}
        </span>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            value={gravity}
            onChange={(e) => setGravity(parseFloat(e.target.value))}
            className="w-32 accent-cyan-400 cursor-pointer h-1 bg-white/20 rounded-lg appearance-none"
          />
          <button
            onClick={handleReset}
            className="px-2 py-1 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-[10px] uppercase font-bold tracking-wider transition-colors"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
